"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Box as Cabinet, Layers, Compass, Lightbulb, LayoutGrid as Grid, Sparkles, Check, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FeaturePillar {
  number: string;
  title: string;
  icon: React.ElementType;
  description: string;
  items: string[];
}
const pillars: FeaturePillar[] = [{
  number: "01",
  title: "Custom cabinetry & millwork",
  icon: Cabinet,
  description: "Bespoke soft-close cabinetry tailored to your ceiling height, workflow, and storage needs.",
  items: ["Custom pantries and pull-out larder units", "Integrated double trash and recycle bins", "Hand-finished solid brass and bronze pulls", "Dovetail drawer boxes with heavy-duty soft-close glides"]
}, {
  number: "02",
  title: "Countertops & stone slabs",
  icon: Layers,
  description: "Precision-mitered stone surfaces sourced from local stone yards and templated to laser accuracy.",
  items: ["Honed quartzite, Calacatta marble, and premium quartz", "Full-height stone backsplash slabs or artisan tile", "Flush under-mount sink integration", "Custom edge details including eased, bullnose, and ogee"]
}, {
  number: "03",
  title: "Layout & flow redesign",
  icon: Compass,
  description: "Architectural planning that reorients work zones to open connection into surrounding living rooms.",
  items: ["Removal or framing of non-load-bearing partitions", "Central island reorientation and prep spacing", "Optimized refrigerator-to-cooktop work triangle", "Engineered structural beam sizing when required"]
}, {
  number: "04",
  title: "Architectural lighting & MEP",
  icon: Lightbulb,
  description: "Layered illumination paired with dedicated circuit upgrades and modern plumbing rough-ins.",
  items: ["Multi-zone 90+ CRI recessed warm LED cans", "Concealed under-cabinet task illumination strips", "Pendant electrical drops centered over island seating", "Dedicated 240V and 120V circuits for pro appliances"]
}, {
  number: "05",
  title: "Hardwood & tile flooring",
  icon: Grid,
  description: "Seamless transitions to adjacent rooms using site-finished hardwoods or heated large-format tile.",
  items: ["Wide-plank white oak laced cleanly into existing floors", "Custom stain matching and matte protective sealers", "Large-format porcelain or limestone floor tile", "Optional in-floor electric radiant heating systems"]
}, {
  number: "06",
  title: "Luxury appliance integration",
  icon: Sparkles,
  description: "Exact architectural rough-in and panel-ready fitting for chef-grade cooking and refrigeration suites.",
  items: ["Integrated panel installations for Sub-Zero and Thermador", "Exterior-vented chimney hoods up to 1,200 CFM", "Counter-depth speed ovens and warming drawers", "Plumbed water lines for ice makers and espresso stations"]
}];
export default function Inclusions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.07
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="whats-included" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Header Section */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16 md:mb-20">
          {/* Eyebrow Badge */}
          <div className="mb-4">
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary-foreground font-semibold px-3.5 py-1 text-xs tracking-widest uppercase rounded-full">
              Full-Scope Execution
            </Badge>
          </div>

          {/* Heading */}
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight mb-5">
            What’s included in your Brookhaven kitchen renovation
          </h2>

          {/* Supporting line */}
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
            Every phase handled under one roof, from initial architectural space
            planning and City of Brookhaven permits to bespoke millwork installation
            and the final walk-through.
          </p>

          {/* Delicate Brushed Gold Hairline Divider */}
          <div className="w-24 h-px bg-primary/50 mt-8 mb-2" />
        </motion.div>

        {/* 6-Card Responsive Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.number} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? undefined : {
            y: -4,
            transition: {
              duration: 0.25,
              ease: [0.2, 0.8, 0.2, 1]
            }
          }} className="group flex flex-col justify-between rounded-2xl bg-card/85 backdrop-blur-md border border-border/80 p-7 sm:p-8 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-md">
                <div>
                  {/* Top Row: Icon with Gold Ring & Number Tag */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center justify-center size-13 rounded-xl bg-secondary text-secondary-foreground shadow-xs ring-1 ring-primary/40 group-hover:ring-primary transition-colors">
                      <Icon className="size-6 text-primary" strokeWidth={1.75} />
                    </div>
                    <span className="font-mono text-xs font-semibold text-muted-foreground/80 tracking-widest bg-muted px-2.5 py-1 rounded-md">
                      {pillar.number}
                    </span>
                  </div>

                  {/* Title & Short Description */}
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight mb-2.5">
                    {pillar.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>

                  {/* Bulleted Inclusion List */}
                  <div className="border-t border-border/60 pt-5 space-y-2.5">
                    {pillar.items.map((item, itemIdx) => <div key={itemIdx} data-index={itemIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                        <div className="size-4 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="size-2.5 stroke-[2.5]" />
                        </div>
                        <span className="leading-snug">{item}</span>
                      </div>)}
                  </div>
                </div>

                {/* Subdued Bottom Indicator */}
                <div className="mt-8 pt-4 border-t border-border/40 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">
                    Precision installation
                  </span>
                  <span className="text-primary font-medium tracking-wide">
                    Brookhaven standard
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Section Trust & Action Strip */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-14 sm:mt-18 rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-10 border border-secondary/80 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
          <div className="flex items-start sm:items-center gap-4 text-left">
            <div className="size-11 rounded-xl bg-primary/20 text-primary flex items-center justify-center shrink-0">
              <ShieldCheck className="size-6 text-primary" strokeWidth={1.75} />
            </div>
            <div>
              <p className="font-serif text-lg sm:text-xl font-semibold text-secondary-foreground">
                Comprehensive project management & permitting
              </p>
              <p className="text-secondary-foreground/75 text-sm mt-0.5 max-w-xl">
                Permit management with DeKalb County and Brookhaven inspectors,
                daily site tidiness, and direct project communication.
              </p>
            </div>
          </div>

          <div className="shrink-0 w-full md:w-auto flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg shadow-sm hover:bg-primary/90 transition-colors w-full sm:w-auto">
              <Link to="/ContactRequestAnEstimatePage">
                <span>Request an estimate</span>
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent px-5 py-3 rounded-lg transition-colors w-full sm:w-auto">
              <Link to="/OurProjectsPage">View recent kitchens</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}